import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MouseIcon from '@mui/icons-material/Mouse';
import ReplayIcon from '@mui/icons-material/Replay';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import {
  TUTORIAL_BUILDER_MESSAGE_SOURCE,
  TUTORIAL_PREVIEW_MESSAGE_SOURCE,
} from '../contexts/TutorialContext';
import {
  addTutorialChild,
  addTutorialPart,
  buildTutorialExport,
  createTutorialNode,
  createTutorialTree,
  findTutorialNode,
  flattenTutorialTree,
  moveTutorialPart,
  parseTutorialPackageUpload,
  removeTutorialNode,
  removeTutorialPart,
  TUTORIAL_UPLOAD_MAX_BYTES,
  updateTutorialNode,
  tutorialTreeFromPackage,
} from '../utils/tutorialBuilder';
import {
  clearWelcomeTutorial,
  createSavedTutorial,
  deleteSavedTutorial,
  getSavedTutorial,
  getSavedTutorials,
  getTutorialRecipients,
  installTutorial,
  setWelcomeTutorial,
  updateSavedTutorial,
} from '../utils/api';


const panelSx = {
  p: 2,
  bgcolor: '#121816',
  border: '2px solid #00E5FF',
  borderRadius: 4,
  boxShadow: '0 7px 0 #006b76, 0 0 18px rgba(0,229,255,.24)',
};

const compactFieldSx = {
  '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: '0.72rem' },
  '& .MuiInputLabel-root': { fontSize: '0.72rem' },
};

const PREVIEW_WIDTHS = {
  desktop: '100%',
  tablet: '768px',
  mobile: '390px',
};

const NumberField = ({ label, value, onChange, min, max, step = 1 }) => (
  <TextField
    label={label}
    type="number"
    value={value}
    onChange={(event) => onChange(Number(event.target.value))}
    inputProps={{ min, max, step }}
    size="small"
    fullWidth
    sx={compactFieldSx}
  />
);

const SelectField = ({ label, value, onChange, options }) => (
  <TextField
    select
    label={label}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    size="small"
    fullWidth
    sx={compactFieldSx}
  >
    {options.map((option) => (
      <MenuItem key={option} value={option}>{option.replaceAll('_', ' ')}</MenuItem>
    ))}
  </TextField>
);

const ControlGrid = ({ children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25 }}>
    {children}
  </Box>
);

const PositionControls = ({ value, onChange, anchors }) => {
  const change = (key, nextValue) => onChange({ ...value, [key]: nextValue });
  const changeNested = (key, nestedKey, nextValue) => onChange({
    ...value,
    [key]: { ...value[key], [nestedKey]: nextValue },
  });

  return (
    <ControlGrid>
      <SelectField label="Anchor" value={value.anchor} onChange={(next) => change('anchor', next)} options={anchors} />
      <SelectField
        label="Placement"
        value={value.placement}
        onChange={(next) => change('placement', next)}
        options={['top', 'right', 'bottom', 'left', 'center']}
      />
      <NumberField
        label="Viewport X %"
        value={value.viewport.x_percent}
        min={0}
        max={100}
        onChange={(next) => changeNested('viewport', 'x_percent', next)}
      />
      <NumberField
        label="Viewport Y %"
        value={value.viewport.y_percent}
        min={0}
        max={100}
        onChange={(next) => changeNested('viewport', 'y_percent', next)}
      />
      <NumberField
        label="Offset X px"
        value={value.offset_px.x}
        min={-2000}
        max={2000}
        onChange={(next) => changeNested('offset_px', 'x', next)}
      />
      <NumberField
        label="Offset Y px"
        value={value.offset_px.y}
        min={-2000}
        max={2000}
        onChange={(next) => changeNested('offset_px', 'y', next)}
      />
      <NumberField
        label="Rotation °"
        value={value.rotation_deg ?? 0}
        min={-360}
        max={360}
        onChange={(next) => change('rotation_deg', next)}
      />
    </ControlGrid>
  );
};

const EditorAccordion = ({ title, children, defaultExpanded = false }) => (
  <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ bgcolor: '#18201C', color: '#00E5FF' }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
      <Typography variant="subtitle2">{title}</Typography>
    </AccordionSummary>
    <AccordionDetails>{children}</AccordionDetails>
  </Accordion>
);

const normalizeFrameRoute = (route) => {
  const value = route.trim();
  if (!value.startsWith('/') || value.startsWith('//')) throw new Error('Preview routes must begin with one /.');
  if (value.startsWith('/tutorial-builder')) throw new Error('The builder cannot preview itself.');
  return value;
};

const TutorialBuilder = () => {
  const initialTreeRef = useRef(null);
  if (!initialTreeRef.current) initialTreeRef.current = createTutorialTree();
  const [tree, setTree] = useState(initialTreeRef.current);
  const [selectedKey, setSelectedKey] = useState(initialTreeRef.current.children[0].key);
  const [selectedPartIndex, setSelectedPartIndex] = useState(0);
  const [frameRouteInput, setFrameRouteInput] = useState('/user-home');
  const [frameRoute, setFrameRoute] = useState('/user-home');
  const [frameLoadRevision, setFrameLoadRevision] = useState(0);
  const [previewReady, setPreviewReady] = useState(false);
  const [picking, setPicking] = useState(false);
  const [previewRevision, setPreviewRevision] = useState(0);
  const [previewPreset, setPreviewPreset] = useState('desktop');
  const [previewViewport, setPreviewViewport] = useState(null);
  const [desktopViewport, setDesktopViewport] = useState(null);
  const [notice, setNotice] = useState(null);
  const [savedTutorials, setSavedTutorials] = useState([]);
  const [savedTutorialId, setSavedTutorialId] = useState(null);
  const [savedRevision, setSavedRevision] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [libraryLoading, setLibraryLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState([]);
  const [assignAllUsers, setAssignAllUsers] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const iframeRef = useRef(null);
  const uploadInputRef = useRef(null);

  const flatTree = useMemo(() => flattenTutorialTree(tree), [tree]);
  const selectedEntry = flatTree.find(({ node }) => node.key === selectedKey) || flatTree[0];
  const selectedNode = selectedEntry.node;
  const isLeaf = selectedNode.children.length === 0;
  const selectedParts = selectedNode.scene?.parts || [];
  const selectedPart = selectedParts[selectedPartIndex] || selectedParts[0] || null;
  const selectedSavedTutorial = savedTutorials.find(
    (tutorial) => tutorial.id === savedTutorialId
  ) || null;

  useEffect(() => setSelectedPartIndex(0), [selectedKey]);

  const updateSelectedNode = useCallback((updater) => {
    setIsDirty(true);
    setTree((currentTree) => {
      const currentNode = findTutorialNode(currentTree, selectedKey);
      return updateTutorialNode(currentTree, selectedKey, updater(currentNode));
    });
  }, [selectedKey]);

  const refreshTutorialLibrary = useCallback(async () => {
    const response = await getSavedTutorials();
    setSavedTutorials(response.tutorials || []);
    return response.tutorials || [];
  }, []);

  useEffect(() => {
    let alive = true;
    refreshTutorialLibrary()
      .catch((error) => {
        if (alive) setNotice({ severity: 'error', text: error.message });
      })
      .finally(() => {
        if (alive) setLibraryLoading(false);
      });
    return () => { alive = false; };
  }, [refreshTutorialLibrary]);

  const updateScene = useCallback((updater) => {
    updateSelectedNode((node) => ({
      ...node,
      scene: {
        ...node.scene,
        parts: node.scene.parts.map((part, index) => (
          index === selectedPartIndex ? updater(part) : part
        )),
      },
    }));
  }, [selectedPartIndex, updateSelectedNode]);

  const updateSequence = useCallback((updater) => {
    updateSelectedNode((node) => ({ ...node, scene: updater(node.scene) }));
  }, [updateSelectedNode]);

  const postToPreview = useCallback((message) => {
    iframeRef.current?.contentWindow?.postMessage({
      source: TUTORIAL_BUILDER_MESSAGE_SOURCE,
      ...message,
    }, window.location.origin);
  }, []);

  useEffect(() => {
    const handlePreviewMessage = (event) => {
      if (
        event.origin !== window.location.origin ||
        event.source !== iframeRef.current?.contentWindow ||
        event.data?.source !== TUTORIAL_PREVIEW_MESSAGE_SOURCE
      ) return;

      if (event.data.type === 'ready') {
        setPreviewReady(true);
        if (!picking) setFrameRouteInput(event.data.route);
      }
      if (['ready', 'viewport'].includes(event.data.type) && event.data.viewport) {
        const nextViewport = {
          width: Number(event.data.viewport.width),
          height: Number(event.data.viewport.height),
        };
        setPreviewViewport(nextViewport);
        if (previewPreset === 'desktop') setDesktopViewport(nextViewport);
      }
      if (event.data.type === 'target-picked') {
        setPicking(false);
        setFrameRouteInput(event.data.route);
        updateScene((scene) => ({
          ...scene,
          [event.data.field === 'advance_on_element_id' ? 'advance_on_element_id' : 'target']:
            event.data.target,
          navigate_to: event.data.route,
        }));
        setPreviewRevision((revision) => revision + 1);
        setNotice({ severity: 'success', text: `Selected #${event.data.target}${event.data.label ? ` — ${event.data.label}` : ''}` });
      }
      if (event.data.type === 'scene-updated' && event.data.payload?.version === 2) {
        updateSequence(() => event.data.payload);
        setSelectedPartIndex(event.data.part_index || 0);
      }
      if (event.data.type === 'preview-part-selected') {
        setSelectedPartIndex(event.data.part_index || 0);
      }
      if (event.data.type === 'pick-cancelled') setPicking(false);
    };
    window.addEventListener('message', handlePreviewMessage);
    return () => window.removeEventListener('message', handlePreviewMessage);
  }, [picking, previewPreset, updateScene, updateSequence]);

  useEffect(() => {
    const referenceViewport = desktopViewport || previewViewport;
    if (!isLeaf || !referenceViewport?.width || !referenceViewport?.height) return;
    const reference = selectedPart?.layout?.reference_viewport;
    if (reference?.width_px && reference?.height_px) return;
    updateScene((scene) => ({
      ...scene,
      layout: {
        responsive: true,
        minimum_scale: 0.35,
        maximum_scale: 1,
        ...(scene.layout || {}),
        reference_viewport: {
          width_px: Math.round(referenceViewport.width),
          height_px: Math.round(referenceViewport.height),
        },
      },
    }));
  }, [desktopViewport, isLeaf, previewViewport, selectedPart?.layout, updateScene]);

  useEffect(() => {
    if (!previewReady || picking || !isLeaf) return;
    postToPreview({
      type: 'preview',
      preview_id: `${selectedNode.key}:${previewRevision}`,
      event_type: selectedNode.eventType,
      payload: selectedNode.scene,
      part_index: selectedPartIndex,
    });
  }, [isLeaf, picking, postToPreview, previewReady, previewRevision, selectedNode, selectedPartIndex]);

  const replaceEditorTree = useCallback((nextTree, tutorial = null) => {
    const entries = flattenTutorialTree(nextTree);
    const initialSelection = entries.find(({ node }) => node.children.length === 0)?.node || nextTree;
    setTree(nextTree);
    setSelectedKey(initialSelection.key);
    setSavedTutorialId(tutorial?.id || null);
    setSavedRevision(tutorial?.revision || null);
    setIsDirty(false);
    setPreviewRevision((revision) => revision + 1);

    const nextRoute = initialSelection.scene?.parts?.[0]?.navigate_to || null;
    setFrameRouteInput(nextRoute || '');
    if (nextRoute && nextRoute !== frameRoute) {
      setPreviewReady(false);
      setPreviewViewport(null);
      setFrameRoute(nextRoute);
      setFrameLoadRevision((revision) => revision + 1);
    }
  }, [frameRoute]);

  const confirmDiscardChanges = () => (
    !isDirty || window.confirm('Discard the unsaved changes to this tutorial?')
  );

  const createNewTutorial = () => {
    if (!confirmDiscardChanges()) return;
    replaceEditorTree(createTutorialTree());
    setNotice({ severity: 'info', text: 'Started a new unsaved tutorial.' });
  };

  const loadTutorial = async (tutorialId) => {
    if (!tutorialId || tutorialId === savedTutorialId) return;
    if (!confirmDiscardChanges()) return;
    try {
      setLibraryLoading(true);
      const response = await getSavedTutorial(tutorialId);
      const tutorial = response.tutorial;
      replaceEditorTree(tutorialTreeFromPackage(tutorial.package), tutorial);
      setNotice({ severity: 'success', text: `Loaded ${tutorial.name} revision ${tutorial.revision}.` });
    } catch (error) {
      setNotice({ severity: 'error', text: error.message });
    } finally {
      setLibraryLoading(false);
    }
  };

  const saveTutorial = async ({ asNew = false } = {}) => {
    try {
      setSaving(true);
      const tutorialPackage = buildTutorialExport(tree);
      const response = asNew || !savedTutorialId
        ? await createSavedTutorial(tree.name, tutorialPackage)
        : await updateSavedTutorial(
          savedTutorialId,
          tree.name,
          tutorialPackage,
          savedRevision,
        );
      const tutorial = response.tutorial;
      await refreshTutorialLibrary();
      replaceEditorTree(tutorialTreeFromPackage(tutorial.package), tutorial);
      setNotice({
        severity: 'success',
        text: `${tutorial.name} saved as revision ${tutorial.revision}.`,
      });
    } catch (error) {
      const suffix = error.status === 409
        ? ' Load the latest revision before saving again.'
        : '';
      setNotice({ severity: 'error', text: `${error.message}${suffix}` });
    } finally {
      setSaving(false);
    }
  };

  const removeSavedTutorial = async () => {
    if (!savedTutorialId) return;
    if (!window.confirm('Delete this saved tutorial? User task copies already assigned will remain.')) return;
    try {
      setSaving(true);
      await deleteSavedTutorial(savedTutorialId);
      await refreshTutorialLibrary();
      replaceEditorTree(createTutorialTree());
      setNotice({ severity: 'success', text: 'Saved tutorial deleted. Installed user copies were kept.' });
    } catch (error) {
      setNotice({ severity: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const openAssignments = async () => {
    if (!savedTutorialId) return;
    try {
      setAssigning(true);
      const response = await getTutorialRecipients(savedTutorialId);
      setRecipients(response.users || []);
      setSelectedRecipientIds([]);
      setAssignAllUsers(false);
      setAssignmentOpen(true);
    } catch (error) {
      setNotice({ severity: 'error', text: error.message });
    } finally {
      setAssigning(false);
    }
  };

  const toggleRecipient = (userId) => {
    setSelectedRecipientIds((current) => current.includes(userId)
      ? current.filter((id) => id !== userId)
      : [...current, userId]);
  };

  const assignTutorial = async () => {
    try {
      setAssigning(true);
      const response = await installTutorial(savedTutorialId, {
        userIds: selectedRecipientIds,
        allUsers: assignAllUsers,
      });
      await refreshTutorialLibrary();
      setAssignmentOpen(false);
      setNotice({
        severity: 'success',
        text: `Assigned ${tree.name} to ${response.installed_count} user${response.installed_count === 1 ? '' : 's'} as independent task copies.`,
      });
    } catch (error) {
      setNotice({ severity: 'error', text: error.message });
    } finally {
      setAssigning(false);
    }
  };

  const toggleWelcomeTutorial = async () => {
    if (!savedTutorialId) return;
    try {
      setSaving(true);
      if (selectedSavedTutorial?.is_welcome) {
        await clearWelcomeTutorial(savedTutorialId);
      } else {
        await setWelcomeTutorial(savedTutorialId);
      }
      await refreshTutorialLibrary();
      setNotice({
        severity: 'success',
        text: selectedSavedTutorial?.is_welcome
          ? `${tree.name} is no longer the welcome tutorial.`
          : `${tree.name} will be added to every new user's to-do list.`,
      });
    } catch (error) {
      setNotice({ severity: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const addChild = () => {
    const child = createTutorialNode();
    setTree((currentTree) => addTutorialChild(currentTree, selectedKey, child));
    setSelectedKey(child.key);
    setIsDirty(true);
  };

  const removeSelected = () => {
    if (selectedKey === tree.key) return;
    setTree((currentTree) => removeTutorialNode(currentTree, selectedKey));
    setSelectedKey(selectedEntry.parentKey || tree.key);
    setIsDirty(true);
  };

  const loadPreviewRoute = () => {
    try {
      const route = normalizeFrameRoute(frameRouteInput);
      postToPreview({ type: 'clear-preview' });
      setPreviewReady(false);
      setPreviewViewport(null);
      setPicking(false);
      setFrameRoute(route);
      setFrameLoadRevision((revision) => revision + 1);
      setNotice(null);
    } catch (error) {
      setNotice({ severity: 'error', text: error.message });
    }
  };

  const pickTarget = (field = 'target') => {
    if (!previewReady || !isLeaf) return;
    setPicking(field);
    setNotice({ severity: 'info', text: 'Click an element inside the preview. Press Escape to cancel.' });
    postToPreview({ type: 'pick-target', field });
  };

  const addPart = () => {
    updateSequence((scene) => addTutorialPart(scene, selectedPartIndex));
    setSelectedPartIndex((index) => index + 1);
  };

  const movePart = (direction) => {
    const nextIndex = selectedPartIndex + direction;
    if (nextIndex < 0 || nextIndex >= selectedParts.length) return;
    updateSequence((scene) => moveTutorialPart(scene, selectedPartIndex, nextIndex));
    setSelectedPartIndex(nextIndex);
  };

  const deletePart = () => {
    if (selectedParts.length <= 1) return;
    updateSequence((scene) => removeTutorialPart(scene, selectedPartIndex));
    setSelectedPartIndex((index) => Math.max(0, Math.min(index, selectedParts.length - 2)));
  };

  const downloadJson = () => {
    try {
      const exported = buildTutorialExport(tree);
      const blob = new Blob([`${JSON.stringify(exported, null, 2)}\n`], { type: 'application/json' });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${tree.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'tutorial'}.json`;
      link.click();
      URL.revokeObjectURL(downloadUrl);
      setNotice({ severity: 'success', text: `Exported ${exported.triggers.length} tutorial scene${exported.triggers.length === 1 ? '' : 's'}.` });
    } catch (error) {
      setNotice({ severity: 'error', text: error.message });
    }
  };

  const uploadJson = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setUploading(true);
      if (file.size > TUTORIAL_UPLOAD_MAX_BYTES) {
        throw new Error('The tutorial JSON file must be 2 MB or smaller.');
      }
      const tutorialPackage = parseTutorialPackageUpload(await file.text());
      const uploadedTree = tutorialTreeFromPackage(tutorialPackage);
      if (!confirmDiscardChanges()) return;
      replaceEditorTree(uploadedTree);
      setIsDirty(true);
      setNotice({
        severity: 'success',
        text: `Uploaded ${tutorialPackage.triggers.length} tutorial scene${tutorialPackage.triggers.length === 1 ? '' : 's'} as a new unsaved tutorial.`,
      });
    } catch (error) {
      setNotice({ severity: 'error', text: error.message });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container maxWidth={false} sx={{ py: 3, pb: 18 }}>
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h3" sx={{ color: '#00E5FF', mb: 1 }}>Tutorial Builder</Typography>
        <Typography variant="body2" sx={{ color: '#FF2D95' }}>
          Create, save, switch, and assign task-driven tutorials against the real app.
        </Typography>
      </Box>

      {notice && <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ mb: 2 }}>{notice.text}</Alert>}

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '280px minmax(480px, 1fr) 390px' },
        gap: 2,
        alignItems: 'start',
      }}>
        <Paper sx={panelSx}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>Tutorial library</Typography>
          <TextField
            select
            SelectProps={{ native: true }}
            label="Saved tutorial"
            value={savedTutorialId || ''}
            onChange={(event) => loadTutorial(event.target.value)}
            disabled={libraryLoading || saving}
            fullWidth
            size="small"
            sx={{ ...compactFieldSx, mb: 1.25 }}
          >
            <option value="">Unsaved tutorial</option>
            {savedTutorials.map((tutorial) => (
              <option key={tutorial.id} value={tutorial.id}>
                {tutorial.name} · r{tutorial.revision}{tutorial.is_welcome ? ' · welcome' : ''}
              </option>
            ))}
          </TextField>
          <Typography variant="caption" sx={{ display: 'block', color: isDirty ? '#FF5F1F' : '#39FF14', mb: 1 }}>
            {savedTutorialId
              ? `Revision ${savedRevision}${isDirty ? ' · unsaved changes' : ''}`
              : `Not saved${isDirty ? ' · unsaved changes' : ''}`}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
            <Button onClick={createNewTutorial} disabled={saving}>New</Button>
            <Button onClick={() => saveTutorial()} disabled={saving} variant="contained">Save</Button>
            <Button onClick={() => saveTutorial({ asNew: true })} disabled={saving}>Save as</Button>
            <Button onClick={removeSavedTutorial} disabled={!savedTutorialId || saving} color="secondary">Delete</Button>
          </Box>
          <Button
            onClick={openAssignments}
            disabled={!savedTutorialId || isDirty || assigning}
            fullWidth
            sx={{ mt: 1 }}
          >
            Assign to users
          </Button>
          <Button
            onClick={toggleWelcomeTutorial}
            disabled={!savedTutorialId || isDirty || saving}
            fullWidth
            variant={selectedSavedTutorial?.is_welcome ? 'contained' : 'outlined'}
            color={selectedSavedTutorial?.is_welcome ? 'success' : 'primary'}
            sx={{ mt: 1 }}
          >
            {selectedSavedTutorial?.is_welcome
              ? 'Remove welcome tutorial'
              : 'Set as welcome tutorial'}
          </Button>
          {selectedSavedTutorial?.is_welcome && (
            <Typography variant="caption" sx={{ display: 'block', color: '#39FF14', mt: 0.75 }}>
              Automatically added to each new user&apos;s to-do list.
            </Typography>
          )}
          {savedTutorialId && isDirty && (
            <Typography variant="caption" sx={{ display: 'block', color: '#FF2D95', mt: 0.75 }}>
              Save changes before assigning or choosing this as the welcome tutorial.
            </Typography>
          )}
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" sx={{ mb: 1.5 }}>Task tree</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
            {flatTree.map(({ node, depth }) => (
              <Button
                key={node.key}
                onClick={() => setSelectedKey(node.key)}
                variant={node.key === selectedKey ? 'contained' : 'outlined'}
                sx={{
                  justifyContent: 'flex-start',
                  ml: `${depth * 14}px`,
                  minWidth: 0,
                  fontSize: '0.64rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {node.children.length ? '▸ ' : '• '}{node.name || 'Untitled'}
              </Button>
            ))}
          </Box>
          <Divider sx={{ mb: 2 }} />
          <TextField
            label="Task name"
            value={selectedNode.name}
            onChange={(event) => updateSelectedNode((node) => ({ ...node, name: event.target.value }))}
            fullWidth
            size="small"
            sx={{ ...compactFieldSx, mb: 1.5 }}
          />
          <NumberField
            label="Duration seconds"
            value={selectedNode.durationSeconds ?? ''}
            min={0}
            onChange={(durationSeconds) => updateSelectedNode((node) => ({ ...node, durationSeconds }))}
          />
          <Box sx={{ display: 'flex', mt: 1.5, gap: 1 }}>
            <Button startIcon={<AddIcon />} onClick={addChild} fullWidth>Add child</Button>
            <IconButton
              aria-label="Delete selected tutorial task"
              disabled={selectedKey === tree.key}
              onClick={removeSelected}
              color="secondary"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, mt: 2 }}>
            <Button
              startIcon={<UploadFileIcon />}
              onClick={() => uploadInputRef.current?.click()}
              disabled={uploading || saving}
              variant="outlined"
            >
              Upload JSON
            </Button>
            <Button
              startIcon={<DownloadIcon />}
              onClick={downloadJson}
              disabled={uploading}
              variant="contained"
            >
              Export JSON
            </Button>
          </Box>
          <input
            ref={uploadInputRef}
            type="file"
            accept=".json,application/json"
            aria-label="Upload tutorial JSON file"
            onChange={uploadJson}
            hidden
          />
        </Paper>

        <Paper sx={{ ...panelSx, p: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <TextField
              label="Preview route"
              value={frameRouteInput}
              onChange={(event) => setFrameRouteInput(event.target.value)}
              size="small"
              sx={{ ...compactFieldSx, flex: 1, minWidth: 220 }}
            />
            <Button onClick={loadPreviewRoute}>Load page</Button>
            <Button
              startIcon={<MouseIcon />}
              onClick={() => pickTarget('target')}
              disabled={!previewReady || !isLeaf || Boolean(picking)}
              variant="contained"
            >
              {picking ? 'Picking…' : 'Pick element'}
            </Button>
            <IconButton
              aria-label="Replay tutorial preview"
              onClick={() => setPreviewRevision((revision) => revision + 1)}
              disabled={!previewReady || !isLeaf}
              color="primary"
            >
              <ReplayIcon />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
            {Object.keys(PREVIEW_WIDTHS).map((preset) => (
              <Button
                key={preset}
                size="small"
                variant={previewPreset === preset ? 'contained' : 'outlined'}
                onClick={() => setPreviewPreset(preset)}
              >
                {preset}
              </Button>
            ))}
            {previewViewport && (
              <Typography variant="caption" sx={{ color: '#FF2D95', ml: 'auto' }}>
                {Math.round(previewViewport.width)} × {Math.round(previewViewport.height)} px
              </Typography>
            )}
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 1.25, color: '#39FF14' }}>
            Drag the sprite directly. Use ↘ to resize, ✥ to move dialogue, T for the tail tip, and B for its bubble attachment.
          </Typography>
          <Box sx={{
            position: 'relative',
            overflowX: 'auto',
            overflowY: 'hidden',
            borderRadius: 3,
            border: `4px solid ${picking ? '#39FF14' : '#00E5FF'}`,
            boxShadow: picking ? '0 0 24px rgba(57,255,20,.7)' : '0 0 20px rgba(0,229,255,.35)',
            bgcolor: '#090D0E',
          }}>
            <Box sx={{ width: PREVIEW_WIDTHS[previewPreset], mx: 'auto' }}>
              <Box
                component="iframe"
                ref={iframeRef}
                key={`${frameRoute}:${frameLoadRevision}`}
                src={frameRoute}
                title="Tutorial page preview"
                sx={{ display: 'block', width: '100%', height: '72vh', minHeight: 620, border: 0, bgcolor: '#090D0E' }}
              />
            </Box>
          </Box>
        </Paper>

        <Paper sx={{ ...panelSx, p: 1.25 }}>
          {!isLeaf ? (
            <Alert severity="info">Add or select a leaf task to author its tutorial scene.</Alert>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  select
                  label="Tutorial part"
                  value={selectedPartIndex}
                  onChange={(event) => setSelectedPartIndex(Number(event.target.value))}
                  size="small"
                  sx={{ ...compactFieldSx, minWidth: 150 }}
                >
                  {selectedParts.map((_part, index) => (
                    <MenuItem key={index} value={index}>Part {index + 1}</MenuItem>
                  ))}
                </TextField>
                <Button onClick={addPart} startIcon={<AddIcon />}>Duplicate part</Button>
                <Button onClick={() => movePart(-1)} disabled={selectedPartIndex === 0}>Move earlier</Button>
                <Button
                  onClick={() => movePart(1)}
                  disabled={selectedPartIndex >= selectedParts.length - 1}
                >
                  Move later
                </Button>
                <IconButton
                  aria-label="Delete selected tutorial part"
                  onClick={deletePart}
                  disabled={selectedParts.length <= 1}
                  color="secondary"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <EditorAccordion title="Scene" defaultExpanded>
                <TextField
                  label="Dialogue"
                  multiline
                  minRows={5}
                  value={selectedPart.message}
                  onChange={(event) => updateScene((scene) => ({ ...scene, message: event.target.value }))}
                  fullWidth
                  sx={{ ...compactFieldSx, mb: 1.5 }}
                />
                <TextField
                  label="Target element ID"
                  value={selectedPart.target || ''}
                  onChange={(event) => updateScene((scene) => ({ ...scene, target: event.target.value || null }))}
                  fullWidth
                  size="small"
                  sx={{ ...compactFieldSx, mb: 1.5 }}
                />
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start' }}>
                  <TextField
                    label="Advance on element ID"
                    value={selectedPart.advance_on_element_id || ''}
                    onChange={(event) => updateScene((scene) => ({
                      ...scene,
                      advance_on_element_id: event.target.value || null,
                    }))}
                    helperText="Optional. Clicking this element advances while preserving its normal action."
                    fullWidth
                    size="small"
                    sx={compactFieldSx}
                  />
                  <Button
                    startIcon={<MouseIcon />}
                    onClick={() => pickTarget('advance_on_element_id')}
                    disabled={!previewReady || Boolean(picking)}
                    variant="outlined"
                  >
                    Pick
                  </Button>
                </Box>
                <TextField
                  label="Navigate to"
                  value={selectedPart.navigate_to || ''}
                  onChange={(event) => updateScene((scene) => ({
                    ...scene,
                    navigate_to: event.target.value || null,
                  }))}
                  helperText="Leave blank to stay on the current page."
                  fullWidth
                  size="small"
                  sx={{ ...compactFieldSx, mb: 1.5 }}
                />
                <SelectField
                  label="Trigger event"
                  value={selectedNode.eventType}
                  onChange={(eventType) => updateSelectedNode((node) => ({ ...node, eventType }))}
                  options={['timer_started', 'timer_stopped', 'completed']}
                />
              </EditorAccordion>

              <EditorAccordion title="Focus">
                <FormControlLabel
                  control={<Checkbox
                    checked={selectedPart.focus.spotlight}
                    onChange={(event) => updateScene((scene) => ({
                      ...scene,
                      focus: { ...scene.focus, spotlight: event.target.checked },
                    }))}
                  />}
                  label="Spotlight target"
                />
                <NumberField
                  label="Target zoom"
                  value={selectedPart.focus.zoom}
                  min={1}
                  max={1.25}
                  step={0.01}
                  onChange={(zoom) => updateScene((scene) => ({ ...scene, focus: { ...scene.focus, zoom } }))}
                />
              </EditorAccordion>

              <EditorAccordion title="Responsive layout">
                <FormControlLabel
                  control={<Checkbox
                    checked={selectedPart.layout?.responsive !== false}
                    onChange={(event) => updateScene((scene) => ({
                      ...scene,
                      layout: { ...scene.layout, responsive: event.target.checked },
                    }))}
                  />}
                  label="Scale down on smaller screens"
                />
                <ControlGrid>
                  <NumberField
                    label="Reference width px"
                    value={selectedPart.layout?.reference_viewport?.width_px ?? ''}
                    min={320}
                    max={3840}
                    onChange={(width_px) => updateScene((scene) => ({
                      ...scene,
                      layout: {
                        ...scene.layout,
                        reference_viewport: { ...scene.layout.reference_viewport, width_px },
                      },
                    }))}
                  />
                  <NumberField
                    label="Reference height px"
                    value={selectedPart.layout?.reference_viewport?.height_px ?? ''}
                    min={240}
                    max={2160}
                    onChange={(height_px) => updateScene((scene) => ({
                      ...scene,
                      layout: {
                        ...scene.layout,
                        reference_viewport: { ...scene.layout.reference_viewport, height_px },
                      },
                    }))}
                  />
                  <NumberField
                    label="Minimum scale"
                    value={selectedPart.layout?.minimum_scale ?? 0.35}
                    min={0.2}
                    max={1}
                    step={0.05}
                    onChange={(minimum_scale) => updateScene((scene) => ({
                      ...scene,
                      layout: { ...scene.layout, minimum_scale },
                    }))}
                  />
                  <NumberField
                    label="Maximum scale"
                    value={selectedPart.layout?.maximum_scale ?? 1}
                    min={0.2}
                    max={2}
                    step={0.05}
                    onChange={(maximum_scale) => updateScene((scene) => ({
                      ...scene,
                      layout: { ...scene.layout, maximum_scale },
                    }))}
                  />
                </ControlGrid>
              </EditorAccordion>

              <EditorAccordion title="Sprite position">
                <PositionControls
                  value={selectedPart.sprite}
                  anchors={['target', 'viewport']}
                  onChange={(sprite) => updateScene((scene) => ({ ...scene, sprite }))}
                />
                <Box sx={{ mt: 1.25 }}>
                  <ControlGrid>
                    <NumberField
                      label="Sprite width px"
                      value={selectedPart.sprite.width_px}
                      min={48}
                      max={240}
                      onChange={(width_px) => updateScene((scene) => ({
                        ...scene,
                        sprite: { ...scene.sprite, width_px },
                      }))}
                    />
                    <SelectField
                      label="Facing"
                      value={selectedPart.sprite.facing}
                      onChange={(facing) => updateScene((scene) => ({
                        ...scene,
                        sprite: { ...scene.sprite, facing },
                      }))}
                      options={['left', 'right']}
                    />
                  </ControlGrid>
                </Box>
              </EditorAccordion>

              <EditorAccordion title="Dialogue position">
                <ControlGrid>
                  <NumberField
                    label="Dialogue width px"
                    value={selectedPart.dialogue.width_px ?? 558}
                    min={240}
                    max={900}
                    onChange={(width_px) => updateScene((scene) => ({
                      ...scene,
                      dialogue: { ...scene.dialogue, width_px },
                    }))}
                  />
                  <NumberField
                    label="Dialogue height px"
                    value={selectedPart.dialogue.height_px ?? 230}
                    min={140}
                    max={600}
                    onChange={(height_px) => updateScene((scene) => ({
                      ...scene,
                      dialogue: { ...scene.dialogue, height_px },
                    }))}
                  />
                  <NumberField
                    label="Dialogue font px"
                    value={selectedPart.dialogue.font_size_px ?? 13}
                    min={8}
                    max={32}
                    onChange={(font_size_px) => updateScene((scene) => ({
                      ...scene,
                      dialogue: { ...scene.dialogue, font_size_px },
                    }))}
                  />
                </ControlGrid>
                <Divider sx={{ my: 1.5 }} />
                <PositionControls
                  value={selectedPart.dialogue}
                  anchors={['sprite', 'target', 'viewport']}
                  onChange={(dialogue) => updateScene((scene) => ({ ...scene, dialogue }))}
                />
                <Box sx={{ mt: 1.25 }}>
                  <SelectField
                    label="Text alignment"
                    value={selectedPart.dialogue.text_align}
                    onChange={(text_align) => updateScene((scene) => ({
                      ...scene,
                      dialogue: { ...scene.dialogue, text_align },
                    }))}
                    options={['left', 'center', 'right']}
                  />
                </Box>
              </EditorAccordion>

              <EditorAccordion title="Speech tail">
                <ControlGrid>
                  <NumberField
                    label="Tip offset X px"
                    value={selectedPart.speech_tail.tip_offset_px.x}
                    min={-2000}
                    max={2000}
                    onChange={(x) => updateScene((scene) => ({
                      ...scene,
                      speech_tail: {
                        ...scene.speech_tail,
                        tip_offset_px: { ...scene.speech_tail.tip_offset_px, x },
                      },
                    }))}
                  />
                  <NumberField
                    label="Tip offset Y px"
                    value={selectedPart.speech_tail.tip_offset_px.y}
                    min={-2000}
                    max={2000}
                    onChange={(y) => updateScene((scene) => ({
                      ...scene,
                      speech_tail: {
                        ...scene.speech_tail,
                        tip_offset_px: { ...scene.speech_tail.tip_offset_px, y },
                      },
                    }))}
                  />
                  <NumberField
                    label="Base offset X px"
                    value={selectedPart.speech_tail.base_offset_px.x}
                    min={-2000}
                    max={2000}
                    onChange={(x) => updateScene((scene) => ({
                      ...scene,
                      speech_tail: {
                        ...scene.speech_tail,
                        base_offset_px: { ...scene.speech_tail.base_offset_px, x },
                      },
                    }))}
                  />
                  <NumberField
                    label="Base offset Y px"
                    value={selectedPart.speech_tail.base_offset_px.y}
                    min={-2000}
                    max={2000}
                    onChange={(y) => updateScene((scene) => ({
                      ...scene,
                      speech_tail: {
                        ...scene.speech_tail,
                        base_offset_px: { ...scene.speech_tail.base_offset_px, y },
                      },
                    }))}
                  />
                  <NumberField
                    label="Tail base width px"
                    value={selectedPart.speech_tail.base_width_px}
                    min={16}
                    max={120}
                    onChange={(base_width_px) => updateScene((scene) => ({
                      ...scene,
                      speech_tail: { ...scene.speech_tail, base_width_px },
                    }))}
                  />
                </ControlGrid>
              </EditorAccordion>

              <EditorAccordion title="Typing and voice">
                <ControlGrid>
                  <NumberField
                    label="Characters / sec"
                    value={selectedPart.typing.chars_per_second}
                    min={1}
                    max={60}
                    onChange={(chars_per_second) => updateScene((scene) => ({
                      ...scene,
                      typing: { ...scene.typing, chars_per_second },
                    }))}
                  />
                  <NumberField
                    label="Punctuation pause"
                    value={selectedPart.typing.period_pause_multiplier}
                    min={1}
                    max={10}
                    step={0.1}
                    onChange={(period_pause_multiplier) => updateScene((scene) => ({
                      ...scene,
                      typing: { ...scene.typing, period_pause_multiplier },
                    }))}
                  />
                  <NumberField
                    label="Voice gain"
                    value={selectedPart.typing.sound_gain}
                    min={0}
                    max={2}
                    step={0.1}
                    onChange={(sound_gain) => updateScene((scene) => ({
                      ...scene,
                      typing: { ...scene.typing, sound_gain },
                    }))}
                  />
                  <NumberField
                    label="Blip ms"
                    value={selectedPart.typing.blip_ms}
                    min={20}
                    max={250}
                    onChange={(blip_ms) => updateScene((scene) => ({
                      ...scene,
                      typing: { ...scene.typing, blip_ms },
                    }))}
                  />
                  <NumberField
                    label="Base pitch Hz"
                    value={selectedPart.typing.base_pitch_hz}
                    min={40}
                    max={2000}
                    onChange={(base_pitch_hz) => updateScene((scene) => ({
                      ...scene,
                      typing: { ...scene.typing, base_pitch_hz },
                    }))}
                  />
                  <NumberField
                    label="Pitch jitter Hz"
                    value={selectedPart.typing.pitch_jitter_hz}
                    min={0}
                    max={1000}
                    onChange={(pitch_jitter_hz) => updateScene((scene) => ({
                      ...scene,
                      typing: { ...scene.typing, pitch_jitter_hz },
                    }))}
                  />
                </ControlGrid>
              </EditorAccordion>

              <EditorAccordion title="Next action">
                <SelectField
                  label="When Next is clicked"
                  value={selectedNode.scene.next_action}
                  onChange={(next_action) => updateSequence((scene) => ({ ...scene, next_action }))}
                  options={['return_to_task', 'dismiss']}
                />
              </EditorAccordion>
            </>
          )}
        </Paper>
      </Box>

      <Dialog
        open={assignmentOpen}
        onClose={() => !assigning && setAssignmentOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Assign {tree.name}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Each recipient receives a separate task tree and separate assigned tasks. Their progress is never shared.
          </Alert>
          <FormControlLabel
            control={(
              <Checkbox
                checked={assignAllUsers}
                onChange={(event) => setAssignAllUsers(event.target.checked)}
              />
            )}
            label={`Assign to all ${recipients.length} existing users`}
          />
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '45vh', overflowY: 'auto' }}>
            {recipients.map((user) => (
              <FormControlLabel
                key={user.id}
                disabled={assignAllUsers}
                control={(
                  <Checkbox
                    checked={assignAllUsers || selectedRecipientIds.includes(user.id)}
                    onChange={() => toggleRecipient(user.id)}
                  />
                )}
                label={(
                  <Box sx={{ py: 0.5 }}>
                    <Typography variant="body2">{user.username}</Typography>
                    <Typography variant="caption" sx={{ color: user.installed ? '#39FF14' : '#FF2D95' }}>
                      {user.installed
                        ? `Installed revision ${user.installed_revision}${user.installed_revision < savedRevision ? ' · update available' : ''}`
                        : 'Not assigned'}
                    </Typography>
                  </Box>
                )}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignmentOpen(false)} disabled={assigning}>Cancel</Button>
          <Button
            onClick={assignTutorial}
            disabled={assigning || (!assignAllUsers && selectedRecipientIds.length === 0)}
            variant="contained"
          >
            {assigning ? 'Assigning…' : 'Assign tutorial'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TutorialBuilder;
